import React, { useEffect, useMemo, useState } from 'react';
import RoleService from '../../services/RoleService';
import utility from '../../utils/utility';
import ReactPaginate from 'react-paginate';
import FilterBar from '../../components/FilterBar';
import { filterList } from '../../utils/filterUtils';

const Role = () => {
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [pageCount, setPageCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;
    const [searchText, setSearchText] = useState('');
    const [formData, setFormData] = useState({
        id: '',
        roleName: '',
        isActive: 1
    });
    const filteredRoles = useMemo(() => filterList(roles, searchText, ['roleName', 'status', 'id']), [roles, searchText]);

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        fetchData();
    }, [currentPage]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const data = await RoleService.getAll(currentPage, itemsPerPage);
            setRoles(data.data || []);
            setPageCount(data.pagination.totalPages || 0);
        } catch (err) {
            utility.showError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (role = null) => {
        if (role) {
            setEditMode(true);
            setFormData({
                id: role.id,
                roleName: role.roleName,
                isActive: role.isActive
            });
        } else {
            setEditMode(false);
            setFormData({
                id: '',
                roleName: '',
                isActive: 1
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: (name === 'isActive') ? parseInt(value) : value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            if (editMode) {
                await RoleService.update(formData.id, formData);
                utility.showSuccess('Role updated successfully');
            } else {
                await RoleService.create(formData);
                utility.showSuccess('Role created successfully');
            }
            handleCloseModal();
            fetchData();
        } catch (err) {
            utility.showError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleToggleStatus = async (role) => {
        try {
            const newStatus = role.isActive ? 0 : 1;
            await RoleService.updateStatus(role.id, newStatus);
            utility.showSuccess(`Role status ${newStatus ? 'activated' : 'deactivated'} successfully`);
            fetchData();
        } catch (err) {
            utility.showError(err.message);
        }
    };

    const handlePageClick = (event) => {
        const selectedPage = event.selected + 1;
        setCurrentPage(selectedPage);
    };

    return (
        <div className="container-fluid py-2">
            <div className="row">
                <div className="col-12 text-end mb-3">
                    <button className="btn bg-gradient-dark mb-0" onClick={() => handleOpenModal()}>
                        <i className="material-symbols-rounded text-sm">add</i>&nbsp;&nbsp;Add New Role
                    </button>
                </div>
                <div className="col-12">
                    <div className="card my-4">
                        <div className="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
                            <div className="bg-gradient-dark shadow-dark border-radius-lg pt-4 pb-3">
                                <h6 className="text-white text-capitalize ps-3">Roles</h6>
                            </div>
                        </div>
                        <div className="card-body px-0 pb-2">
                            <div className="px-3">
                                <FilterBar
                                    value={searchText}
                                    onChange={setSearchText}
                                    onClear={() => setSearchText('')}
                                    placeholder="Search roles..."
                                />
                            </div>
                            <div className="table-responsive p-0">
                                {loading ? (
                                    <div className="p-4 text-center">Loading...</div>
                                ) : (
                                    <table className="table align-items-center mb-0">
                                        <thead>
                                            <tr>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Role Name</th>
                                                <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Status</th>
                                                <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredRoles.length === 0 ? (
                                                <tr><td colSpan="3" className="text-center py-4">No roles found.</td></tr>
                                            ) : (
                                                filteredRoles.map((role) => (
                                                    <tr key={role.id}>
                                                        <td>
                                                            <div className="d-flex px-2 py-1">
                                                                <div className="d-flex flex-column justify-content-center">
                                                                    <h6 className="mb-0 text-sm">{role.roleName}</h6>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="align-middle text-center text-sm">
                                                            <span
                                                                className={`badge badge-sm bg-gradient-${role.isActive ? 'success' : 'secondary'} cursor-pointer`}
                                                                onClick={() => handleToggleStatus(role)}
                                                            >
                                                                {role.isActive ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </td>
                                                        <td className="align-middle text-center">
                                                            <button className="btn btn-link text-info mb-0" onClick={() => handleOpenModal(role)}>
                                                                Edit
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colSpan="3">
                                                    <ReactPaginate
                                                        forcePage={currentPage - 1}
                                                        breakLabel="..."
                                                        nextLabel=">"
                                                        onPageChange={handlePageClick}
                                                        pageRangeDisplayed={3}
                                                        marginPagesDisplayed={2}
                                                        pageCount={pageCount}
                                                        previousLabel="<"
                                                        containerClassName="pagination"
                                                        pageClassName="page-item"
                                                        pageLinkClassName="page-link"
                                                        previousClassName="page-item"
                                                        previousLinkClassName="page-link"
                                                        nextClassName="page-item"
                                                        nextLinkClassName="page-link"
                                                        activeClassName="active"
                                                        disabledClassName="disabled"
                                                        breakClassName="page-item"
                                                        breakLinkClassName="page-link"
                                                    />
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header border-0">
                                <h5 className="modal-title font-weight-normal">{editMode ? 'Edit Role' : 'Add New Role'}</h5>
                                <button type="button" className="btn-close text-dark" onClick={handleCloseModal}>
                                    <span aria-hidden="true">&times;</span>
                                </button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body row">
                                    <div className="col-md-12 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Role Name *</label>
                                            <input type="text" name="roleName" className="form-control" value={formData.roleName} onChange={handleInputChange} required />
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer border-0">
                                    <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>Cancel</button>
                                    <button type="submit" className="btn bg-gradient-dark" disabled={saving}>
                                        {saving ? 'Saving...' : (editMode ? 'Update Role' : 'Create Role')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Role;